import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Box, Button, ButtonSpinner, ButtonText, Center, HStack, Icon, Pressable, Spinner, Text, VStack,
} from '@gluestack-ui/themed';
import { ChevronLeft, X } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useCloseOralExamCohort } from '@/common/api/endpoints/oral-exams/oral-exams';
import { OralExamOperationalFault } from '@/common/api/models/oralExamOperationalFault';
import { FAULT_PRESENTATION } from '@/common/oralExam/faultPresentation';
import { RECORDING_NOT_REQUIRED_INFO } from '@/common/oralExam/recordingGateCopy';
import { useMeetingConfirm } from '@/common/components/meeting/MeetingConfirm';
import { useDialog } from '@/common/context/DialogContext';
import { useOralExamCohortState } from '@/common/hooks/oralExam/useOralExamCohortState';
import { getOralExamApiErrorMessage } from '@/common/hooks/oralExam/oralExamApiError';

import { MeetingControlBar } from '@/common/components/meeting/MeetingControlBar';
import { MeetingPublishCameraGuard } from '@/common/components/meeting/MeetingPublishCameraGuard';
import { MeetingSessionShell } from '@/common/components/meeting/MeetingSessionShell';
import { MeetingStage } from '@/common/components/meeting/MeetingStage';
import type { MeetingJoinSettings } from '@/common/components/meeting/types';
import { useMeetingLobbyState } from '@/common/components/meeting/useMeetingLobbyState';
import { useLiveKitTokenRequest } from '@/common/hooks/oralExam/useLiveKitTokenRequest';
import { useOralExamMutations } from '@/common/hooks/oralExam/useOralExamMutations';
import { useOralExamRunStatePoll } from '@/common/hooks/oralExam/useOralExamRunStatePoll';
import type { OralExamRouteIds } from '@/common/hooks/oralExam/useOralExamRunStatePoll';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import type { LiveKitJoinHandle } from '@/student/module/hooks/useEventTaskSlots';
import { isAtLastOralExamQuestion, isOneToOnePeerGoneSurface } from '@/common/utils/oralExam/oneToOnePeerGone';
import { hasInProgressOralExamRuns, isOneToOneExamSurface } from '@/common/utils/oralExam/runConduct';
import { OralExamErrorState } from '@/common/components/oralExam/OralExamErrorState';
import { OperationalFaultStudentBanner } from '@/student/module/components/OperationalFaultStudentBanner';

import { LecturerConductingStage } from './LecturerConductingStage';
import { LecturerPreflightStage } from './LecturerPreflightStage';
import { OralExamConductingControls, OralExamControlPanel } from './OralExamControlPanel';

function LecturerOperationalFaultBanner({
  fault,
}: {
  fault: OralExamOperationalFault;
}) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const presentation = FAULT_PRESENTATION[fault];
  const { lecturerLine } = presentation;

  if (dismissed) return null;

  return (
    <Box
      bg="$statusWarningSubtle"
      borderWidth={1}
      borderColor="$statusWarning"
      borderRadius="$sm"
      px="$4"
      py="$3"
      testID="lecturer-operational-fault-banner"
    >
      <HStack space="sm" alignItems="flex-start" justifyContent="space-between">
        <VStack flex={1} space="sm">
          <Text flex={1} size="sm" color="$statusError" testID="lecturer-operational-fault-banner-text">
            {t(lecturerLine.copyKey, { defaultValue: lecturerLine.defaultValue })}
          </Text>
        </VStack>
        <Pressable
          onPress={() => setDismissed(true)}
          accessibilityRole="button"
          accessibilityLabel={t('common.buttons.dismiss', { defaultValue: 'Dismiss' })}
          testID="lecturer-operational-fault-banner-dismiss"
        >
          <Icon as={X} size="sm" color="$textSubtle" />
        </Pressable>
      </HStack>
    </Box>
  );
}

export type LecturerOralExamSessionProps = {
  ids: OralExamRouteIds;
  slotId: string;
  liveKitJoin: LiveKitJoinHandle;
  onClose: () => void | Promise<void | boolean>;
  /** When provided, lobby state is owned by the parent (e.g. cohort session). */
  lobbyDone?: boolean;
  joinSettings?: MeetingJoinSettings;
  onLobbyJoin?: (settings: MeetingJoinSettings) => void;
  /** When set, renders a breadcrumb bar above the stage that calls onClose. */
  backLabel?: string;
  /** 1:N drop-in: hide hang-up; overview-back returns without a confirm. */
  isDropIn?: boolean;
};

/**
 * Full-screen lecturer oral exam: meeting room, video stage, and exam control panel.
 */
export function LecturerOralExamSession({
  ids,
  slotId,
  liveKitJoin,
  onClose,
  lobbyDone: lobbyDoneProp,
  joinSettings: joinSettingsProp,
  onLobbyJoin: onLobbyJoinProp,
  backLabel,
  isDropIn = false,
}: LecturerOralExamSessionProps) {
  const { t } = useTranslation();
  const {
    joinCreds,
    joinError,
    refetch,
    retry,
    isLoading,
    clearInSessionTokenRetryWait,
  } = useLiveKitTokenRequest(liveKitJoin);
  const internalLobby = useMeetingLobbyState();
  const lobbyDone = lobbyDoneProp ?? internalLobby.lobbyDone;
  const joinSettings = joinSettingsProp ?? internalLobby.joinSettings;
  const onLobbyJoin = onLobbyJoinProp ?? internalLobby.onLobbyJoin;
  const [inRoom, setInRoom] = useState(true);

  const {
    runState, isLoading: stateLoading, queryKey,
  } = useOralExamRunStatePoll(ids, { enabled: inRoom && lobbyDone, polling: inRoom && lobbyDone });

  const { completeIfAbsent } = useOralExamMutations(ids, {
    etag: runState?.etag ?? null,
    stateQueryKey: queryKey,
  });
  const queryClient = useQueryClient();
  const { confirm } = useMeetingConfirm();
  const { showToastMessage } = useDialog();
  const {
    bookingRuns, cohort, queryKey: cohortQueryKey,
  } = useOralExamCohortState(slotId, {
    polling: inRoom && lobbyDone,
  });
  const cohortEtagRef = useRef(cohort?.etag);
  cohortEtagRef.current = cohort?.etag;
  const bookingRunsRef = useRef(bookingRuns);
  bookingRunsRef.current = bookingRuns;
  const finishSessionInFlightRef = useRef(false);

  const closeCohortMutation = useCloseOralExamCohort({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: cohortQueryKey });
      },
    },
  });

  const handleConfirmFinishSession = useCallback(async () => {
    if (finishSessionInFlightRef.current) return;
    finishSessionInFlightRef.current = true;
    try {
      const etag = cohortEtagRef.current;
      if (!etag) {
        throw new Error('Missing cohort etag');
      }
      await closeCohortMutation.mutateAsync({
        slotId,
        data: null,
        headers: { 'If-Match': etag },
      });
      showToastMessage('success', t('oralExam.lecturer.cohort.endSuccess', {
        defaultValue: 'Exam ended for the cohort.',
      }));
    } catch (error) {
      const status = error instanceof AxiosError ? error.response?.status : undefined;
      if (status === 412) {
        queryClient.invalidateQueries({ queryKey: cohortQueryKey });
        showToastMessage('info', t('oralExam.staleRetry', {
          defaultValue: 'The session changed — please try ending the exam again.',
        }));
        return;
      }
      showToastMessage(
        'error',
        getOralExamApiErrorMessage(error, t('oralExam.actionFailed')),
      );
    } finally {
      finishSessionInFlightRef.current = false;
    }
  }, [closeCohortMutation, cohortQueryKey, queryClient, showToastMessage, slotId, t]);

  const handleRequestFinishSession = useCallback(async () => {
    if (closeCohortMutation.isPending || finishSessionInFlightRef.current) return;
    if (hasInProgressOralExamRuns(bookingRunsRef.current)) {
      const confirmed = await confirm({
        title: t('oralExam.lecturer.cohort.endDialogTitle', { defaultValue: 'End exam' }),
        message: t('oralExam.lecturer.cohort.endDialogBody', {
          defaultValue:
            'End the exam for the whole cohort? Students still in progress will be stopped.',
        }),
        confirmLabel: t('oralExam.lecturer.cohort.endDialogConfirm', { defaultValue: 'End exam' }),
        destructive: true,
      });
      if (!confirmed) return;
    }
    await handleConfirmFinishSession();
  }, [closeCohortMutation.isPending, confirm, handleConfirmFinishSession, t]);

  const approveLeave = useCallback((): Promise<boolean | void> => (
    Promise.resolve(onClose())
  ), [onClose]);

  const handleAfterDisconnect = useCallback(() => {
    setInRoom(false);
  }, []);

  const handleBackToCohort = useCallback(async () => {
    try {
      const shouldLeave = await approveLeave();
      if (shouldLeave !== false) {
        setInRoom(false);
      }
    } catch (error) {
      showToastMessage(
        'error',
        getOralExamApiErrorMessage(error, t('oralExam.actionFailed')),
      );
    }
  }, [approveLeave, showToastMessage, t]);

  const handleMeetingLeave = useCallback(() => {
    if (isDropIn) {
      handleBackToCohort().catch(() => undefined);
      return;
    }
    handleAfterDisconnect();
  }, [handleAfterDisconnect, handleBackToCohort, isDropIn]);

  const inProgress = runState?.state === 'in_progress';
  const isOneToOne = isOneToOneExamSurface(runState);
  const awaitingExamStart = Boolean(
    inProgress && isOneToOne && !runState?.currentQuestion?.question?.trim(),
  );
  const isTerminal = Boolean(
    runState && (runState.state === 'completed' || runState.state === 'aborted'),
  );
  const showFinishSession = Boolean(
    runState
    && isTerminal
    && runState.sessionPhase === OralExamSessionPhase.in_exam
    && !isOneToOne,
  );

  const shouldLeaveMeeting = Boolean(
    runState?.sessionPhase === OralExamSessionPhase.post_exam
    || (isOneToOne && isTerminal),
  );

  // 1:1 End can complete the run while cohort is still in_exam (seal failure
  // before auto-close). Parent only mounts wrap-up on post_exam — close cohort.
  const needsStuckCohortClose = Boolean(
    isOneToOne
    && isTerminal
    && runState?.sessionPhase === OralExamSessionPhase.in_exam,
  );
  const stuckCloseAttemptedRef = useRef(false);

  useEffect(() => {
    if (shouldLeaveMeeting) {
      setInRoom(false);
      queryClient.invalidateQueries({ queryKey: cohortQueryKey });
    }
  }, [cohortQueryKey, queryClient, shouldLeaveMeeting]);

  useEffect(() => {
    if (!needsStuckCohortClose || stuckCloseAttemptedRef.current) return;
    if (closeCohortMutation.isPending || finishSessionInFlightRef.current) return;
    const etag = cohortEtagRef.current ?? cohort?.etag;
    if (!etag) return;

    stuckCloseAttemptedRef.current = true;
    finishSessionInFlightRef.current = true;
    closeCohortMutation.mutateAsync({
      slotId,
      data: null,
      headers: { 'If-Match': etag },
    }).then(() => {
      queryClient.invalidateQueries({ queryKey: cohortQueryKey });
    }).catch((error: unknown) => {
      stuckCloseAttemptedRef.current = false;
      const status = error instanceof AxiosError ? error.response?.status : undefined;
      if (status === 412) {
        queryClient.invalidateQueries({ queryKey: cohortQueryKey });
        showToastMessage('info', t('oralExam.staleRetry', {
          defaultValue: 'The session changed — please try ending the exam again.',
        }));
        return;
      }
      showToastMessage(
        'error',
        getOralExamApiErrorMessage(error, t('oralExam.actionFailed')),
      );
    }).finally(() => {
      finishSessionInFlightRef.current = false;
    });
  }, [
    closeCohortMutation,
    cohort?.etag,
    cohortQueryKey,
    needsStuckCohortClose,
    queryClient,
    showToastMessage,
    slotId,
    t,
  ]);

  if (joinError) {
    return (
      <OralExamErrorState
        title={t('oralExam.errorState.joinFailedTitle', {
          defaultValue: 'Could not join the exam room',
        })}
        message={t('oralExam.joinFailed')}
        retry={retry}
      />
    );
  }

  if (!joinCreds && isLoading) {
    return (
      <Center flex={1} p="$4">
        <Spinner size="large" />
        <Text size="sm" color="$textSubtle" mt="$2">{t('oralExam.connecting')}</Text>
      </Center>
    );
  }

  if (!joinCreds) {
    return (
      <Center flex={1} p="$4">
        <Text color="$statusError">{t('oralExam.joinFailed')}</Text>
      </Center>
    );
  }

  if (shouldLeaveMeeting) {
    return (
      <Center flex={1} p="$4" testID="lecturer-leaving-meeting">
        <Spinner size="large" />
        <Text size="sm" color="$textSubtle" mt="$2">
          {t('oralExam.lecturer.openingWrapUp', {
            defaultValue: 'Opening wrap-up…',
          })}
        </Text>
      </Center>
    );
  }

  const isPreFlight = runState?.sessionPhase === OralExamSessionPhase.pre_flight;
  const agentFault = runState?.operationalFault;
  const showRecordingInfo = runState?.recordingRequired === false;
  const showRecordingFailed = agentFault === OralExamOperationalFault.recording_failed;
  const showAgentFaultBanner = showRecordingFailed
    || agentFault === OralExamOperationalFault.failover_required
    || agentFault === OralExamOperationalFault.agent_failed;
  return (
    <MeetingSessionShell
      token={joinCreds.token}
      serverUrl={joinCreds.livekitUrl}
      roomName={joinCreds.roomName}
      participantRole="lecturer"
      lobbyDone={lobbyDone}
      joinSettings={joinSettings}
      onLobbyJoin={onLobbyJoin}
      onLeave={handleMeetingLeave}
      onRejoin={refetch}
      onConnected={clearInSessionTokenRetryWait}
      skipLeaveConfirm={isTerminal}
    >
      <MeetingPublishCameraGuard>
        <VStack flex={1} space="md" p="$3" overflow="hidden">
          {backLabel && !isDropIn ? (
            <HStack
              alignItems="center"
              h={40}
              px="$3"
              borderBottomWidth={1}
              borderBottomColor="$borderLight"
            >
              <Pressable
                onPress={() => { handleBackToCohort().catch(() => undefined); }}
                testID="lecturer-back-to-cohort"
                accessibilityRole="button"
              >
                <HStack alignItems="center" space="xs">
                  <Icon as={ChevronLeft} size="sm" color="$textSubtle" />
                  <Text size="sm" color="$textSubtle">{backLabel}</Text>
                </HStack>
              </Pressable>
            </HStack>
          ) : null}
          {showRecordingInfo ? (
            <OperationalFaultStudentBanner
              copyKey={RECORDING_NOT_REQUIRED_INFO.copyKey}
              defaultValue={RECORDING_NOT_REQUIRED_INFO.defaultValue}
              tone="info"
              testID="lecturer-recording-not-required-banner"
            />
          ) : null}
          {showAgentFaultBanner && agentFault ? (
            <LecturerOperationalFaultBanner
              fault={agentFault}
            />
          ) : null}
          {inProgress && !awaitingExamStart ? (
            <LecturerConductingStage
              studentId={
                bookingRuns.find((run) => run.bookingId === ids.bookingId)?.studentId
              }
              question={runState?.currentQuestion?.question}
              questionId={runState?.currentQuestion?.questionId}
              secondsPerQuestion={runState?.secondsPerQuestion}
              questionMarkerStartedAt={runState?.questionMarkerStartedAt}
              questionDeadline={runState?.questionDeadline}
              isPaused={runState?.isPaused ?? false}
              isDropIn={isDropIn}
              questionIndex={
                typeof runState?.currentQuestionIndex === 'number'
                  ? runState.currentQuestionIndex + 1
                  : null
              }
              totalQuestions={runState?.selectedQuestionIds?.length ?? null}
              enablePeerGoneBanner={isOneToOnePeerGoneSurface(runState)}
              isLastQuestion={isAtLastOralExamQuestion(runState)}
              completeIfAbsent={completeIfAbsent}
            />
          ) : (
            <MeetingStage />
          )}
          {runState ? (
            isPreFlight ? (
              <LecturerPreflightStage slotId={slotId} />
            ) : (
              <OralExamControlPanel ids={ids} runState={runState} stateQueryKey={queryKey} />
            )
          ) : stateLoading ? (
            <Box p="$3" alignItems="center">
              <Spinner size="small" />
            </Box>
          ) : null}
          {showFinishSession ? (
            <HStack justifyContent="center" pb="$2">
              <Button
                size="md"
                action="primary"
                onPress={handleRequestFinishSession}
                isDisabled={closeCohortMutation.isPending}
                testID="oral-exam-finish-session"
              >
                {closeCohortMutation.isPending ? <ButtonSpinner mr="$1" /> : null}
                <ButtonText>
                  {t('oralExam.lecturer.finishSession', { defaultValue: 'Finish session' })}
                </ButtonText>
              </Button>
            </HStack>
          ) : null}
          <MeetingControlBar
            skipLeaveConfirm={isTerminal}
            hideScreenShare={inProgress && (isOneToOne || isDropIn)}
            hideLeave={(inProgress && isOneToOne) || isDropIn}
            conductingControls={inProgress && runState && !awaitingExamStart ? (
              <OralExamConductingControls
                ids={ids}
                runState={runState}
                stateQueryKey={queryKey}
                isDropIn={isDropIn}
                onReturnToOverview={isDropIn ? handleBackToCohort : undefined}
              />
            ) : undefined}
          />
        </VStack>
      </MeetingPublishCameraGuard>
    </MeetingSessionShell>
  );
}
